/**
 * LineSelector Component
 * 
 * PURPOSE: Allows users to select which production line they're working at
 * This is the PRIMARY line selection mechanism for workflows
 * 
 * BUSINESS LOGIC:
 * - Users may have access to multiple lines (e.g., Quality manager oversees Line 1 and Line 2)
 * - All data queries are filtered by selected line (enforced by RLS)
 * - Line selection persists in localStorage (convenience, not security)
 * - If user has only 1 line, auto-select it (no dropdown needed)
 * 
 * SECURITY:
 * - Frontend selection is convenience only
 * - Backend RLS policies enforce actual line access
 * - User cannot bypass RLS by manipulating localStorage
 * 
 * UI/UX:
 * - Shown in header/navbar for easy line switching
 * - Displays line hierarchy (factory/area/line)
 * - Visual indicator of current line
 */

import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Building2 } from 'lucide-react';

interface Line {
  id: string;
  name: string;
  display_name: string;
  short_display_name: string;
  factory_id: string;
  factory_name: string;
  area_id: string;
  area_name: string;
}

interface SiteSelectorProps {
  /**
   * Callback when site selection changes
   * WHY: Parent components (AuthContext) need to know when site changes
   * to refresh data and update application state
   */
  onSiteChange: (siteId: string) => void;
  
  /**
   * Currently selected site ID
   * WHY: Controlled component pattern - state lives in parent
   */
  selectedSiteId?: string;
  
  /**
   * Optional className for styling
   */
  className?: string;

  /**
   * Force showing dropdown even if only one site is available
   * Defaults to hiding when only a single site exists to reduce clutter
   */
  showWhenSingle?: boolean;
}

export const SiteSelector = ({
  onSiteChange,
  selectedSiteId,
  className,
  showWhenSingle = false,
}: SiteSelectorProps) => {
  const { t } = useTranslation();
  const { allLines, accessibleLineIds, currentFactoryId, currentAreaId } = useAuth();

  /**
   * Use all sites from AuthContext to show full list
   *
   * WHY:
   * - Users can see all sites in the system
   * - Non-accessible sites are greyed out
   * - Provides visibility into the full organization structure
   */
  const lines = allLines.filter((line) => {
    if (currentFactoryId && line.factory_id !== currentFactoryId) return false;
    if (currentAreaId && line.area_id !== currentAreaId) return false;
    return true;
  });
  const selectedLine = lines.find(line => line.id === selectedSiteId);

  const handleSiteChange = (lineId: string) => {
    // Only allow selecting accessible lines
    if (accessibleLineIds.has(lineId)) {
      onSiteChange(lineId);
    }
  };

  // Don't show selector if user has no sites
  // WHY: No choice to make = no UI clutter
  if (lines.length === 0) {
    return null;
  }

  if (!showWhenSingle && lines.length <= 1) {
    return null;
  }

  return (
    <div className={className}>
      <Select value={selectedSiteId} onValueChange={handleSiteChange}>
        <SelectTrigger className="w-[280px]">
          <Building2 className="mr-2 h-4 w-4" />
          <span className={`truncate text-left ${selectedLine ? '' : 'text-muted-foreground'}`}>
            {selectedLine?.name || t('siteSelector.selectLine')}
          </span>
        </SelectTrigger>
        <SelectContent>
          {lines.map((line) => {
            const isAccessible = accessibleLineIds.has(line.id);
            const linePath = `${line.factory_name} / ${line.area_name}`;
            return (
              <SelectItem
                key={line.id}
                value={line.id}
                disabled={!isAccessible}
                className={!isAccessible ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <div className="flex flex-col">
                  <span className={`font-medium ${!isAccessible ? 'text-muted-foreground' : ''}`}>
                    {line.name}
                    {!isAccessible && ` ${t('siteSelector.noAccessSuffix')}`}
                  </span>
                  <span className="text-xs text-muted-foreground">{linePath}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
